import { useCallback } from "react";
import api from "utils/api";
import { useInfiniteCursorRequest } from "hooks/useInfiniteCursorRequest";
import type { ApiProject, DashboardProject, ProjectEdit } from "../types";
import type { UseDashboardProjectsArgs } from "./types";

const PROJECTS_PAGE_SIZE = 20;

const mapProjects = (projects: ApiProject[]): DashboardProject[] =>
  projects.map((project) => ({
    id: project._id,
    title: project.title,
    description: project.description,
    status: project.status,
    createdAt: project.createdAt,
    archivedAt: project.archivedAt,
    organizationId: project.organizationId,
    organizationName: project.organizationName,
  }));

export const useDashboardProjects = ({
  view,
  organization,
}: UseDashboardProjectsArgs) => {
  const {
    items,
    setItems,
    isLoading,
    error,
    hasMore: canLoadMore,
    loadMore: onLoadMore,
  } = useInfiniteCursorRequest<DashboardProject>({
    limit: PROJECTS_PAGE_SIZE,
    resetKey: `${view}:${organization ?? "all"}`,
    service: async ({ cursor, limit }) => {
      const data = await api.fetchProjects({
        cursor,
        limit,
        archived: view === "archived",
        organization,
      });
      return {
        items: mapProjects(data.projects ?? []),
        nextCursor: data.nextCursor ?? null,
        hasMore: data.hasMore ?? false,
      };
    },
  });

  // Patch the accumulated list in place so loaded pages and scroll survive.
  const removeProject = useCallback(
    (projectId: string) => {
      setItems((previousProjects) =>
        previousProjects.filter((project) => project.id !== projectId),
      );
    },
    [setItems],
  );

  const updateProject = useCallback(
    (projectId: string, changes: ProjectEdit) => {
      setItems((previousProjects) =>
        previousProjects.map((project) =>
          project.id === projectId ? { ...project, ...changes } : project,
        ),
      );
    },
    [setItems],
  );

  return {
    projects: items,
    isLoading,
    error,
    onLoadMore,
    canLoadMore,
    removeProject,
    updateProject,
  };
};
